import React, { useState } from 'react';
import { Box, Paper, Typography, Chip, IconButton, Collapse } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import AnomalyRow from './AnomalyRow';
import BrandBreakdownMatrix from './BrandBreakdownMatrix';
import SeverityBrandDropdown from './SeverityBrandDropdown';
import type { AnomalyResult, Severity, Quadrant } from './types';
import type { DashboardFilters } from '../Dashboard/DashboardFilterBar';


const SEVERITY_RANK: Record<Severity, number> = {
  green: 0, unknown: 0, orange: 1, red: 2,
};

const BORDER: Record<Severity, string> = {
  green:   'var(--color-success, #16A34A)',
  orange:  'var(--color-warning, #CA8A04)',
  red:     'var(--color-danger, #DC2626)',
  unknown: 'var(--color-border, #ddd)',
};


function worstSeverity(anomalies: AnomalyResult[]): Severity {
  let worst: Severity = 'green';
  for (const a of anomalies) {
    if (SEVERITY_RANK[a.severity] > SEVERITY_RANK[worst]) worst = a.severity;
  }
  return worst;
}


type SectionKey = 'red' | 'orange' | 'green';

const SECTION_META: Record<SectionKey, { label: string; color: string; dot: string }> = {
  red:    { label: 'Immediate action · high business impact', color: 'var(--color-danger, #DC2626)',  dot: '🔴' },
  orange: { label: 'Watch out',                                color: 'var(--color-warning, #CA8A04)', dot: '🟠' },
  green:  { label: 'Under control',                            color: 'var(--color-success, #16A34A)', dot: '🟢' },
};

function groupBySeverity(anomalies: AnomalyResult[]): Record<SectionKey, AnomalyResult[]> {
  const out: Record<SectionKey, AnomalyResult[]> = { red: [], orange: [], green: [] };
  for (const a of anomalies) {
    if (a.severity === 'red') out.red.push(a);
    else if (a.severity === 'orange') out.orange.push(a);
    else out.green.push(a); // green + unknown
  }
  const byScore = (x: AnomalyResult, y: AnomalyResult) =>
    (y.priority_score ?? 0) - (x.priority_score ?? 0) || (y.count ?? 0) - (x.count ?? 0);
  out.red.sort(byScore); out.orange.sort(byScore); out.green.sort(byScore);
  return out;
}

const SeveritySection: React.FC<{
  sectionKey: SectionKey;
  rules: AnomalyResult[];
  quadrant: Quadrant;
  filters: DashboardFilters;
  defaultExpanded: boolean;
}> = ({ sectionKey, rules, quadrant, filters, defaultExpanded }) => {
  const [open, setOpen] = useState(defaultExpanded);
  const meta = SECTION_META[sectionKey];
  return (
    <Box data-testid={`section-${sectionKey}`} sx={{ mb: 1, borderTop: '1px solid var(--color-border, #eee)', pt: 0.75 }}>
      <Box
        role="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
      >
        <Box component="span">{meta.dot}</Box>
        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: meta.color }}>{meta.label}</Typography>
        <Typography sx={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
          ({rules.length} {rules.length === 1 ? 'rule' : 'rules'})
        </Typography>
        <ExpandMoreIcon sx={{ fontSize: 18, ml: 'auto', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />
      </Box>
      <Collapse in={open} unmountOnExit={false}>
        {rules.map((a) => <AnomalyRow key={a.id} anomaly={a} />)}
        <SeverityBrandDropdown quadrant={quadrant} severity={sectionKey} filters={filters} />
      </Collapse>
    </Box>
  );
};


interface Props {
  title: string;
  quadrant: Quadrant;
  filters: DashboardFilters;
  anomalies: AnomalyResult[];
}

const QuadrantCard: React.FC<Props> = ({ title, quadrant, filters, anomalies }) => {
  const [expanded, setExpanded] = useState(false);
  const worst = worstSeverity(anomalies);
  const sections = groupBySeverity(anomalies);
  const sorted = [...anomalies].sort(
    (a, b) => (b.priority_score ?? 0) - (a.priority_score ?? 0)
      || (b.count ?? 0) - (a.count ?? 0)
  );
  const actionable = anomalies.filter((a) => a.priority !== 'none');
  const rank = { P1: 3, P2: 2, P3: 1, none: 0 } as const;
  const topBucket = actionable.reduce<string>(
    (top, a) => (rank[a.priority] > rank[top as keyof typeof rank] ? a.priority : top),
    'none'
  );
  const rollupLabel = actionable.length === 0
    ? 'All clear'
    : `${topBucket} · ${actionable.length} to act on`;
  const ROLLUP_COLOR: Record<string, { bg: string; fg: string }> = {
    P1: { bg: 'var(--color-danger-bg, #FEF2F2)', fg: 'var(--color-danger, #DC2626)' },
    P2: { bg: 'var(--color-warning-bg, #FEFCE8)', fg: 'var(--color-warning, #CA8A04)' },
    P3: { bg: 'var(--color-bg-tertiary, #EDEDE8)', fg: 'var(--color-text-secondary, #4B4B47)' },
  };
  const rollupColor = actionable.length === 0
    ? { bg: 'var(--color-success-bg, #F0FDF4)', fg: 'var(--color-success, #16A34A)' }
    : ROLLUP_COLOR[topBucket] ?? ROLLUP_COLOR.P3;
  return (
    <Paper
      data-worst-severity={worst}
      elevation={0}
      sx={{
        p: 2, height: '100%',
        border: '1px solid var(--color-border, #ddd)',
        borderLeft: `4px solid ${BORDER[worst]}`,
        borderRadius: 'var(--radius-md, 6px)',
        bgcolor: 'var(--color-surface, #fff)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip size="small" label={rollupLabel} sx={{ bgcolor: rollupColor.bg, color: rollupColor.fg, fontWeight: 600 }} />
          <IconButton
            size="small"
            aria-label={expanded ? 'Hide brand detail' : 'Show brand detail'}
            onClick={() => setExpanded((v) => !v)}
            sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}
          >
            <ExpandMoreIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
      {anomalies.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <SeveritySection sectionKey="red"    rules={sections.red}    quadrant={quadrant} filters={filters} defaultExpanded />
          <SeveritySection sectionKey="orange" rules={sections.orange} quadrant={quadrant} filters={filters} defaultExpanded={false} />
          <SeveritySection sectionKey="green"  rules={sections.green}  quadrant={quadrant} filters={filters} defaultExpanded={false} />
        </Box>
      )}
      {anomalies.length === 0 ? (
        <Typography sx={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
          No rules registered yet
        </Typography>
      ) : (
        <Box>
          {sorted.map((a) => <AnomalyRow key={a.id} anomaly={a} />)}
        </Box>
      )}
      <Collapse in={expanded} unmountOnExit={false}>
        <BrandBreakdownMatrix quadrant={quadrant} filters={filters} enabled={expanded} />
      </Collapse>
    </Paper>
  );
};

export default QuadrantCard;
